import API from "@/utils/api/index";

const TXID_OK = /^[^\s]{3,256}$/;

export function validateTxid(txid) {
  const clean = String(txid || "").trim();
  if (!TXID_OK.test(clean)) throw new Error("Invalid TX format");
  return clean;
}

function toMsg(err) {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    "Unable to check"
  );
}

export async function fetchDispute(txid) {
  const clean = validateTxid(txid);

  // 1) Try your DB-aware endpoint first — handles DUPLICATE_RISK, LOW_BALANCE etc.
  try {
    const { data } = await API.get("/disputes/status", {
      params: { txid: clean, _: Date.now() },
      headers: { Accept: "application/json" },
    });

    // Normalize to the shape DisputeModal expects
    // { transactionId, status, step, message, label?, actionable?, supportEmail? }
    if (data?.status) {
      return {
        transactionId: data.transactionId || clean,
        status: normalizeInternalStatus(data.status, data.deliveryStatus),
        step: resolveStep(data.status, data.deliveryStatus),
        message: data.message || resolveMessage(data.status),
        label: data.label || null,
        actionable: data.actionable || false,
        supportEmail: data.supportEmail || null,
        source: data.source || 'internal',
      };
    }
  } catch (err) {
    // 404 = not in our DB → fall through to Zendit
    if (err?.response?.status !== 404) {
      const e = new Error(toMsg(err));
      e.status = err?.response?.status;
      throw e;
    }
  }

  // 2) Fall back to Zendit (delivery TXID lookup)
  try {
    const { data } = await API.get("/disputes", {
      params: { txid: clean, _: Date.now() },
      headers: { Accept: "application/json" },
    });
    return data;
  } catch (err) {
    const e = new Error(toMsg(err));
    e.status = err?.response?.status;
    throw e;
  }
}

// Map your internal providerStatus → the PENDING/IN_PROGRESS/DONE/FAIL
// shape that DisputeModal's Badge + Progress already understand
function normalizeInternalStatus(providerStatus, deliveryStatus) {
  const s = String(providerStatus || '').toUpperCase();
  const d = String(deliveryStatus || '').toUpperCase();

  if (d === 'DELIVERED' || s === 'SUCCESS' || s === 'DONE') return 'DONE';
  if (s === 'DUPLICATE_RISK' || s === 'COST_UNKNOWN' || s === 'PAYMENT_UNCONFIRMED') return 'FAIL';
  if (s === 'LOW_BALANCE') return 'IN_PROGRESS'; // not a failure — will retry
  if (d === 'BLOCKED') return 'FAIL';
  if (d === 'SUBMITTED' || d === 'PROCESSING') return 'IN_PROGRESS';
  if (d === 'PENDING') return 'PENDING';
  if (s === 'FAILED') return 'FAIL';

  return 'PENDING';
}

function resolveStep(providerStatus, deliveryStatus) {
  const normalized = normalizeInternalStatus(providerStatus, deliveryStatus);
  return { PENDING: 1, AUTHORIZED: 2, IN_PROGRESS: 3, DONE: 4, FAIL: 4 }[normalized] ?? 0;
}

function resolveMessage(providerStatus) {
  const s = String(providerStatus || '').toUpperCase();
  const map = {
    DUPLICATE_RISK: 'This order was held — a similar order was already delivered recently. Contact support if you need assistance.',
    LOW_BALANCE: 'Payment received. Delivery is temporarily queued — no action needed, we will process it shortly.',
    COST_UNKNOWN: 'Payment received but this order requires manual review. Contact support with your Transaction ID.',
    PAYMENT_UNCONFIRMED: 'Payment could not be verified. Contact support with your Transaction ID.',
    SUCCESS: 'Transaction completed successfully.',
    DONE: 'Transaction completed successfully.',
    FAILED: 'Transaction failed. Contact support.',
  };
  return map[s] || 'Status unavailable.';
}